import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useBranch } from '@/features/branch/branch-context';
import { useInvalidateQueue } from '@/features/queue/api';
import { api } from '@/lib/api';

export type CalendarEventType =
  | 'public_holiday'
  | 'doctor_leave'
  | 'clinic_closure'
  | 'short_hours'
  | 'ramadan_hours';

/** Labels as on the Admin v2 "Add to the calendar" chips and month legend. */
export const EVENT_LABELS: Record<CalendarEventType, string> = {
  doctor_leave: 'Doctor leave',
  clinic_closure: 'Clinic closure',
  short_hours: 'Short hours',
  public_holiday: 'Public holiday',
  ramadan_hours: 'Ramadan hours',
};

/** `POST /calendar/events` body (CreateCalendarEventDto). */
export interface CalendarEventInput {
  type: CalendarEventType;
  from: string;
  to: string;
  /** null = both branches. */
  branchId: string | null;
  /** Doctor leave only. */
  doctorStaffId?: string;
  description: string;
  /** Short hours only. */
  opens?: string;
  closes?: string;
}

export interface CalendarEvent {
  id: string;
  type: CalendarEventType;
  from: string;
  to: string;
  branch: { id: string; name: string } | null;
  doctor: { id: string; fullName: string } | null;
  description: string;
  shortHours: string | null;
  affectedCount: number;
  createdAt: string;
  /** Only in the `POST /calendar/events` response (RL-1046). */
  affected?: CalendarAffected;
}

/**
 * RL-1046: the bookings counted in `affectedCount`, by reason. Only the
 * "asked to rebook" patients are told; "any doctor" bookings on leave days
 * and bookings already checked in are counted for the desk to check.
 */
export interface CalendarAffected {
  total: number;
  askedToRebook: number;
  anyDoctor: number;
  checkedIn: number;
}

export interface MonthView {
  month: string;
  today: string;
  fridayNote: string | null;
  events: CalendarEvent[];
  days: {
    date: string;
    weekday: number;
    bookings: number;
    events: { id: string; type: CalendarEventType; description: string }[];
    fridayNote: string | null;
  }[];
}

/** RL-1662: booked, future bookings the opening hours no longer allow. */
export interface OutsideHours {
  count: number;
  /** The branch's time zone, to show the times in (review fix; older APIs omit it). */
  timezone?: string;
  /** Up to 200, in time order. */
  bookings: {
    appointmentId: string;
    startsAt: string;
    reason: 'OUTSIDE_OPENING_HOURS' | 'FRIDAY_PRAYERS';
    patient: { id: string; fullName: string };
    service: { id: string; name: string };
  }[];
}

export interface BranchHours {
  branch: { id: string; code: string; name: string };
  hours: {
    opens: string;
    closes: string;
    fridayBreakStart: string | null;
    fridayBreakEnd: string | null;
    ramadanFirst: string | null;
    ramadanSecond: string | null;
  } | null;
  updatedAt: string | null;
  /** RL-1662: only on PUT /calendar/hours/{branchId}; older APIs omit it. */
  outsideHours?: OutsideHours;
}

export interface DepositRules {
  services: {
    id: string;
    code: string;
    name: string;
    depositSen: number | null;
  }[];
  depositAfterNoShows: number | null;
}

export interface NoShowRow {
  patient: { id: string; fullName: string };
  noShows: number;
  lastNoShowAt: string | null;
  depositNeeded: boolean;
}

export interface WaitlistEntry {
  id: string;
  number: string;
  patient: { id: string; fullName: string };
  service: { id: string; name: string };
  branch: { id: string; name: string };
  doctor: { id: string; fullName: string } | null;
  wants: string;
  fromDate: string | null;
  toDate: string | null;
  status: 'waiting' | 'offered' | 'booked' | 'removed';
  offer: { startsAt: string; expiresAt: string } | null;
  booked: { appointmentId: string; startsAt: string } | null;
  since: string;
  version: number;
}

function useBranchKey() {
  return useBranch().selected?.id ?? 'all';
}

export function useMonth(month: string) {
  const branch = useBranchKey();
  return useQuery({
    queryKey: ['calendar', 'month', branch, month],
    queryFn: async ({ signal }) =>
      (
        await api.get<MonthView>('/calendar/month', {
          signal,
          query: { month },
        })
      ).data,
  });
}

/** RL-1663 review: GET /calendar/hours/{branchId}/outside (calendar:manage). */
export function useOutsideHours(branchId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['calendar', 'outside', branchId],
    queryFn: async ({ signal }) =>
      (
        await api.get<OutsideHours>(`/calendar/hours/${branchId}/outside`, {
          signal,
          headers: { 'X-Branch-Id': branchId },
        })
      ).data,
    enabled,
  });
}

export function useHours() {
  return useQuery({
    queryKey: ['calendar', 'hours'],
    queryFn: async ({ signal }) =>
      (await api.get<BranchHours[]>('/calendar/hours', { signal })).data,
  });
}

export function useDepositRules() {
  return useQuery({
    queryKey: ['calendar', 'deposit-rules'],
    queryFn: async ({ signal }) =>
      (await api.get<DepositRules>('/calendar/deposit-rules', { signal })).data,
  });
}

export function useNoShows() {
  const branch = useBranchKey();
  return useQuery({
    queryKey: ['calendar', 'no-shows', branch],
    queryFn: async ({ signal }) =>
      (
        await api.get<{
          depositAfterNoShows: number | null;
          patients: NoShowRow[];
        }>('/calendar/no-shows', { signal })
      ).data,
  });
}

export function useWaitlist() {
  const branch = useBranchKey();
  return useQuery({
    queryKey: ['waitlist', branch],
    queryFn: async ({ signal }) =>
      (await api.get<WaitlistEntry[]>('/waitlist', { signal })).data,
    refetchInterval: 30_000,
  });
}

export function useCalendarActions() {
  const qc = useQueryClient();
  const invalidateQueue = useInvalidateQueue();
  const done = {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['calendar'] });
      void qc.invalidateQueries({ queryKey: ['waitlist'] });
      void invalidateQueue();
    },
  };
  return {
    addEvent: useMutation({
      mutationFn: async (input: CalendarEventInput) =>
        (await api.post<CalendarEvent>('/calendar/events', input)).data,
      ...done,
    }),
    removeEvent: useMutation({
      mutationFn: async (id: string) =>
        (await api.post(`/calendar/events/${id}/remove`)).data,
      ...done,
    }),
    saveHours: useMutation({
      mutationFn: async (v: {
        branchId: string;
        opens?: string;
        closes?: string;
        fridayBreakStart?: string | null;
        fridayBreakEnd?: string | null;
        ramadanFirst?: string | null;
        ramadanSecond?: string | null;
      }) => {
        const { branchId, ...body } = v;
        return (await api.put<BranchHours>(`/calendar/hours/${branchId}`, body))
          .data;
      },
      ...done,
    }),
    // RL-1662: "Ask them to rebook" for bookings outside the new hours.
    rebookOutsideHours: useMutation({
      mutationFn: async (branchId: string) =>
        (
          await api.post<OutsideHours & { asked: number }>(
            `/calendar/hours/${branchId}/rebook-notify`,
          )
        ).data,
      ...done,
    }),
    saveDepositRules: useMutation({
      mutationFn: async (input: {
        services: { serviceId: string; depositSen: number | null }[];
        depositAfterNoShows: number | null;
      }) =>
        (await api.put<DepositRules>('/calendar/deposit-rules', input)).data,
      ...done,
    }),
    addWaitlist: useMutation({
      mutationFn: async (input: {
        patientId: string;
        serviceId: string;
        doctorStaffId?: string;
        wants: string;
        fromDate?: string;
        toDate?: string;
      }) => (await api.post<WaitlistEntry>('/waitlist', input)).data,
      ...done,
    }),
    acceptOffer: useMutation({
      mutationFn: async (id: string) =>
        (await api.post<WaitlistEntry>(`/waitlist/${id}/accept`)).data,
      ...done,
    }),
    removeWaitlist: useMutation({
      mutationFn: async (id: string) =>
        (await api.post(`/waitlist/${id}/remove`)).data,
      ...done,
    }),
  };
}
